import type { EventHookTrigger } from '@vueuse/core';

export type Action = {
  kill: () => void;
  name: string;
  play: () => Promise<void>;
  onlyOnce: boolean;
  minDelayCall?: number;
};

export const useAbstractAction = (
  name: string,
  onlyOnce: boolean = false,
  minDelayCall: number = -1,
  requiredActions: string[] = [],
  waitBeforePlay: number = -1,
  autoRegister: boolean = true
) => {
  const actions = useActionsStore();

  const route = useRoute();

  const completeEvent = createEventHook<any>();

  const playEvent = createEventHook<any>();

  completeEvent.on((success) => {
    actions.completeAction(name, success);
  });

  const isRootPath = computed(() => {
    let depth = 2;
    if (route.path.includes('revisions')) {
      depth = 3;
    }
    return route.path.split('/').length <= depth;
  });

  const _getIsRequiredActionsDone = () => {
    return !requiredActions
      .filter((actionName) => actionName !== name)
      .some((actionName) => !actions.actionHasBeenPlayed(actionName));
  };

  const kill = () => {
    completeEvent.clear();
    playEvent.clear();
  };

  const complete = (success: boolean = true) => {
    completeEvent.trigger(success);
  };

  const play = async () => {
    if (waitBeforePlay > 0) {
      await until(ref).toBe(true, { timeout: waitBeforePlay });
    }
    playEvent.trigger();
  };

  const action: Action = {
    name,
    kill,
    onlyOnce,
    play,
    minDelayCall,
  };

  const registerAction = () => {
    if (_getIsRequiredActionsDone()) {
      actions.enqueue(action);
    }
  };

  if (autoRegister) {
    registerAction();
  }

  return {
    actions,
    name,
    kill,
    onComplete: completeEvent.on,
    completeEvent,
    complete,
    onPlay: playEvent.on,
    isRootPath,
    registerAction, // used for actionHook to be placed at the end of other actions
  };
};
